import React, { FC, useEffect, useState } from 'react';

import { Box, Button, Stack } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useGetAccountsIsAgent } from 'features/accounts/api';
import { useGetUsers } from 'features/users/api';

import { DownloadIcon } from 'shared/assets/icons';
import { REGEX } from 'shared/constants';
import { TAccounts } from 'shared/services/types/accounts.types';
import { TUsers } from 'shared/services/types/users.types';
import { TWithdrawal } from 'shared/services/types/withdrawal.types';
import { FormWrapper } from 'shared/ui/form-wrapper';
import { Input } from 'shared/ui/input';
import { ModalTemplate } from 'shared/ui/modal-template';
import { SelectSearch } from 'shared/ui/select-multi';
import { hasFormValuesChanged } from 'shared/utils';
import { addMessageToast } from 'shared/utils/addMessageToast';

import { useCreateWithdrawal } from './api';
import { accountBalance, accountsAvailableOptions, usersAvailableOptions } from './helpers';

interface IProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
const defaultFilterAccs = {
  size: 1000,
  page: 0
} as TAccounts.GetAccount;

const defaultFilterUsers = {
  size: 1000,
  page: 0
} as TUsers.GetUsers;

export const WithdrawalModal: FC<IProps> = ({ setIsOpen, isOpen }) => {
  const { method_id } = useParams();
  const methodID = parseInt(method_id!);

  const methods = useForm({ mode: 'onBlur' });
  const [userID, setUserID] = useState();
  const accountFrom = methods.watch().account_id_from;
  const {
    formState: { isDirty, isValid }
  } = methods;

  const { accountsData, isAccountsLoading } = useGetAccountsIsAgent({
    ...defaultFilterAccs,
    user_id: userID,
    method_id: +methodID!
  } as TAccounts.GetAccountByUserAndMethod);

  const { usersData, isUsersLoading } = useGetUsers({
    ...defaultFilterUsers,
    method_id: method_id
  });
  const balance = accountBalance(accountFrom, accountsData);
  const maxWithdrawal = balance || 20000;

  const isConfirmationNeeded = hasFormValuesChanged(methods.watch(), {});

  const { create } = useCreateWithdrawal();
  const { reset } = methods;

  const onSubmit: SubmitHandler<TWithdrawal.WithdrawalCreate> = async data => {
    try {
      await create.mutateAsync(data);
      addMessageToast('Withdrawal create', 'success');
      setIsOpen(false);
    } catch (error) {
      addMessageToast(error);
    }
  };

  const withdrawalAccounts = accountsAvailableOptions(accountsData);
  const withdrawalUsers = usersAvailableOptions(usersData);

  useEffect(() => {
    setUserID(methods.watch('user_id'));
  }, [methods.watch()]);

  return (
    <ModalTemplate
      isConfirmationNeeded={isConfirmationNeeded}
      close={() => {
        reset();
        setIsOpen(false);
      }}
      isOpen={isOpen}
      titleText={'Add withdrawal'}
      reset={reset}
      sx={{
        '.MuiPaper-root.close-modal': {
          width: '568px'
        }
      }}>
      <FormWrapper onSubmit={onSubmit} methods={methods}>
        <Stack direction={'column'} gap={15} flexWrap={'nowrap'}>
          <SelectSearch
            name='user_id'
            inputLabel='Select user*'
            inputPlaceholder='Select user'
            rules={{
              required: 'Please, select user'
            }}
            onChangeSelect={e => {
              if (!Array.isArray(e)) {
                methods.resetField('account_id_from');
              }
            }}
            options={withdrawalUsers ?? []}
            isOptionsLoading={isUsersLoading}
          />
          {withdrawalAccounts?.length === 1 ? (
            <Input
              name='account_id_from'
              autofill
              label='Select account*'
              placeholder='Select account'
              defaultValue={withdrawalAccounts[0].value.toString()}></Input>
          ) : (
            <SelectSearch
              name='account_id_from'
              disabled={!userID || isAccountsLoading}
              inputLabel='Select account*'
              inputPlaceholder='Select account'
              rules={{
                validate: value => {
                  if (!value) return 'Please, select account';
                }
              }}
              options={withdrawalAccounts ?? []}
              isOptionsLoading={isAccountsLoading}
            />
          )}
          <Input
            name='amount'
            label='Amount'
            placeholder='Enter amount'
            rules={{
              required: 'Please, enter amount',
              min: {
                value: 1,
                message: 'Amount must be at least 1'
              },
              max: {
                value: maxWithdrawal,
                message: `Amount must less than or equal to ${maxWithdrawal}`
              },
              pattern: {
                value: REGEX.INTEGER_POS,
                message: 'Please enter a valid amount(only integer allowed)'
              }
            }}
          />
          <Input
            name='account_id_to'
            label='Requisite accounts'
            placeholder='Enter requisite accounts'
            rules={{
              required: 'Please, enter requisite',
              pattern: {
                value: REGEX.INTEGER_POS,
                message: 'Please enter a valid requisite(only digits allowed)'
              },
              minLength: {
                value: 3,
                message: 'Too short requisite accounts'
              }
            }}
          />
          <Input
            name='auth_code'
            label='Two-factor authentication*'
            placeholder='6-digit code'
            rules={{
              required: 'Please, enter 6-digit-code.',
              pattern: { value: REGEX.TWO_FACTOR, message: 'Please, enter 6-digit-code.' }
            }}
          />
        </Stack>

        <Box display={'flex'} gap='10px' mt={15} justifyContent='center'>
          <Button
            type='submit'
            fullWidth
            disabled={!isConfirmationNeeded || !isValid}
            variant='contained'
            sx={{ borderRadius: 50, textTransform: 'none', height: 40, width: 1 }}
            startIcon={<DownloadIcon />}>
            Send
          </Button>
        </Box>
      </FormWrapper>
    </ModalTemplate>
  );
};
